import compact from "lodash/compact";
import intersection from "lodash/intersection";
import React, { ComponentType, FC, useMemo } from "react";
import { useMatch } from "react-router-dom";
import useCurrentPlayerQuery from "~@/api/queries/useCurrentPlayerQuery";
import PlayerInactiveModal from "~@/components/layout/PlayerInactiveModal";
import useParentsPlayersQuery from "~@/api/queries/useParentsPlayersQuery";
import { useAuth } from "./withAuthenticationRequired";

interface PlayerActiveWrapperProps {
  children: React.ReactElement;
}

const PlayerActiveWrapper: React.FC<PlayerActiveWrapperProps> = ({
  children,
}) => {
  const { data: playerData, isLoading: isLoadingPlayer } =
    useCurrentPlayerQuery();
  const { data: parentsPlayersData, isLoading: isLoadingParent } =
    useParentsPlayersQuery();
  const { user } = useAuth();
  const match1 = useMatch("/");
  const match2 = useMatch("/schedule");
  const match3 = useMatch("/tournament-table");

  const isAdminRole = useMemo(() => {
    const allowedRoles = [
      "SUPER_ADMIN",
      "ADMIN",
      "CONTENT_MANAGER",
      "FINANCE_MANAGER",
      "BASKETBALL_COACH",
      "BASKETBALL_STAT",
    ];
    return intersection(compact(user?.roles), allowedRoles).length > 0;
  }, [user]);

  const isPublicRoute = !!(match1 || match2 || match3);
  const isActiveParent = parentsPlayersData?.some(({ isActive }) => isActive);
  const showModal =
    !isLoadingPlayer &&
    !isLoadingParent &&
    !isAdminRole &&
    !isActiveParent &&
    playerData?.isActive !== true &&
    !isPublicRoute;

  return (
    <>
      {children}
      {showModal && <PlayerInactiveModal open />}
    </>
  );
};

export const withPlayerActiveRequired = <P extends object>(
  Component: ComponentType<P>,
): FC<P> => {
  const WithPlayerActiveRequired: FC<P> = (props) => {
    return (
      <PlayerActiveWrapper>
        <Component {...props} />
      </PlayerActiveWrapper>
    );
  };

  WithPlayerActiveRequired.displayName = `withPlayerActiveRequired(${
    Component.displayName || Component.name
  })`;

  return WithPlayerActiveRequired;
};
